import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import { useAsync } from '../../pages/aai/useAsync';
import { POPUP_BLOCKED, openMcpSignIn, prepareMcpSignIn } from './mcpSignIn';
import { McpPermissions } from './McpPermissions';
import { RemoveMcpConfirm } from './RemoveMcpConfirm';
import type { McpAuth, MyMcpView, PersonalMcpServer } from '../../types/aai';
import { Action, Chip, EmptyState, ErrorNote, Field, FieldGrid, Input, Kicker, Loading, Modal, RouteMark, Select } from '../aa';

const HEAD = 'px-3 py-2.5 text-left text-[12.5px] font-medium text-ink-muted';
const CELL = 'px-3 py-3 text-[13.5px] text-ink';

/*
 * MCP servers, for the employee.
 *
 * Everyone adds and connects their own servers, on their own sign-in, and
 * sets what the AI may do with each one for themselves. Nothing is shared and
 * nobody is asked. Below them, the company's servers they already reach.
 */

const AUTH_LABEL: Record<McpAuth, string> = {
  none: 'No sign-in',
  bearer: 'Bearer token',
  header: 'Key in a header',
  oauth: 'Sign in with the provider',
};

const detailOf = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || fallback;

interface Draft {
  label: string;
  url: string;
  auth: McpAuth;
  header: string;
  secret: string;
  description: string;
}

const EMPTY: Draft = { label: '', url: '', auth: 'bearer', header: 'X-API-Key', secret: '', description: '' };

type DraftField = 'label' | 'url' | 'secret';

/* What is missing or wrong, field by field, shown under each one. */
const errorsFor = (d: Draft): Partial<Record<DraftField, string>> => {
  const out: Partial<Record<DraftField, string>> = {};
  if (!d.label.trim()) out.label = 'Give it a name.';
  const url = d.url.trim();
  if (url && !/^https:\/\//.test(url)) out.url = 'The address must start with https://';
  if (!url) out.url = 'Give its address. It starts with https://';
  if ((d.auth === 'bearer' || d.auth === 'header') && !d.secret.trim()) out.secret = 'Paste the token or key the server gave you.';
  return out;
};

export const ServerDialog: React.FC<{ onClose: () => void; onDone: () => void }> = ({ onClose, onDone }) => {
  const [d, setD] = useState<Draft>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState<Partial<Record<DraftField, boolean>>>({});
  const [tried, setTried] = useState(false);
  const set = (patch: Partial<Draft>) => setD((prev) => ({ ...prev, ...patch }));
  const errors = errorsFor(d);
  const shown = (f: DraftField) => {
    if (tried || touched[f]) return errors[f];
    return undefined;
  };
  const touch = (f: DraftField) => setTouched((prev) => ({ ...prev, [f]: true }));

  const save = async () => {
    if (Object.keys(errors).length > 0) {
      setTried(true);
      return;
    }
    setBusy(true);
    try {
      let header: string | undefined;
      if (d.auth === 'header') header = d.header.trim();
      let secret: string | undefined;
      if (d.auth === 'bearer' || d.auth === 'header') secret = d.secret.trim();
      const { data: added } = await aaiApi.addMyMcp({
        label: d.label.trim(), url: d.url.trim(), auth: d.auth, header, description: d.description.trim(), secret,
      });
      if (d.auth === 'oauth') toast.success(`${d.label.trim()} is added. Click Sign in on it to finish.`);
      else toast.success(`${d.label.trim()} is ready in your next new chat.`);
      if (added.warning) toast.warn(added.warning);
      onDone();
    } catch (err) {
      toast.error(detailOf(err, 'Could not add that server.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal size="dialog" onClose={onClose} context={<Kicker>Add your own MCP server</Kicker>} footer={(
      <div className="flex justify-end gap-2">
        <Action variant="quiet" onClick={onClose}>Cancel</Action>
        <Action disabled={busy} reason="Adding" onClick={save}>Add</Action>
      </div>
    )}>
      <h2 className="m-0 text-[24px] font-bold leading-[31px] tracking-[-0.015em] text-ink">Add an MCP server</h2>
      <p className="m-0 mt-2 text-[14px] leading-[23px] text-ink-muted">
        It runs in your own sessions on your own sign-in. Nobody else sees it or uses it, and you set what the AI may do with it.
      </p>
      <div className="mt-6">
        <FieldGrid>
          <Field label="Name" htmlFor="my-mcp-name" marker="required" error={shown('label')} hint="What you call it, e.g. DeepWiki.">
            <Input id="my-mcp-name" value={d.label} onChange={(e) => set({ label: e.target.value })} onBlur={() => touch('label')} placeholder="DeepWiki" autoComplete="off" />
          </Field>
          <Field label="Address" htmlFor="my-mcp-url" marker="required" error={shown('url')} hint="The server's MCP address, starting with https://">
            <Input id="my-mcp-url" type="url" value={d.url} onChange={(e) => set({ url: e.target.value })} onBlur={() => touch('url')} placeholder="https://mcp.deepwiki.com/mcp" autoComplete="off" />
          </Field>
          <Field label="Sign-in" htmlFor="my-mcp-auth">
            <Select id="my-mcp-auth" value={d.auth} onChange={(e) => set({ auth: e.target.value as McpAuth })}>
              <option value="bearer">{AUTH_LABEL.bearer}</option>
              <option value="header">{AUTH_LABEL.header}</option>
              <option value="oauth">{AUTH_LABEL.oauth}</option>
              <option value="none">{AUTH_LABEL.none}</option>
            </Select>
          </Field>
          {d.auth === 'header' && (
            <Field label="Header name" htmlFor="my-mcp-header"><Input id="my-mcp-header" value={d.header} onChange={(e) => set({ header: e.target.value })} /></Field>
          )}
          {d.auth === 'oauth' && (
            <p className="m-0 self-end text-[12.5px] leading-[19px] text-ink-dim">No token to paste. After adding, click Sign in on it and approve on the provider&apos;s screen.</p>
          )}
          {(d.auth === 'bearer' || d.auth === 'header') && (
            <Field label="Your token or key" htmlFor="my-mcp-secret" marker="required" error={shown('secret')} hint="Stored encrypted, used only in your sessions.">
              <Input id="my-mcp-secret" name="mcp-token" type="password" value={d.secret} onChange={(e) => set({ secret: e.target.value })} onBlur={() => touch('secret')} autoComplete="new-password" />
            </Field>
          )}
          <Field label="What it does" htmlFor="my-mcp-desc" className="sm:col-span-2" marker="optional" hint="One line shown under the server in your list.">
            <Input id="my-mcp-desc" value={d.description} onChange={(e) => set({ description: e.target.value })} placeholder="Answers questions about GitHub repositories" />
          </Field>
        </FieldGrid>
      </div>
    </Modal>
  );
};

export const EmployeeMcpPanel: React.FC = () => {
  const view = useAsync<MyMcpView>(() => aaiApi.listMyMcp(), 'Could not load MCP servers.');
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState('');
  const [signingIn, setSigningIn] = useState('');
  const [permsFor, setPermsFor] = useState<{ key: string; label: string } | null>(null);
  const [removing, setRemoving] = useState<PersonalMcpServer | null>(null);

  if (view.loading) return <Loading label="Loading MCP servers" />;
  if (view.error || !view.data) return <ErrorNote message={view.error || 'Could not load MCP servers.'} />;
  const data = view.data;

  const remove = (s: PersonalMcpServer) => setRemoving(s);
  const reload = () => view.reload();

  const confirmRemove = async (s: PersonalMcpServer) => {
    setBusy(s.key);
    try {
      await aaiApi.removeMyMcp(s.key);
      toast.success(`${s.label} is out of your sessions.`);
      setRemoving(null);
      reload();
    } catch (err) {
      toast.error(detailOf(err, `Could not remove ${s.label}.`));
    } finally {
      setBusy('');
    }
  };

  const signIn = async (s: PersonalMcpServer) => {
    const popup = prepareMcpSignIn();
    if (!popup) {
      toast.error(POPUP_BLOCKED);
      return;
    }
    setSigningIn(s.key);
    try {
      const { data } = await aaiApi.beginMyMcpOauth(s.key);
      openMcpSignIn(popup, data.authorizeUrl, (ok) => {
        setSigningIn('');
        if (ok) toast.success(`${s.label} is signed in.`);
        reload();
      });
    } catch (err) {
      popup.close();
      setSigningIn('');
      toast.error(detailOf(err, `Could not start the sign-in for ${s.label}.`));
    }
  };

  const statusOf = (s: PersonalMcpServer): React.ReactNode => {
    if (s.connected) return <Chip tone="ok">Ready to use</Chip>;
    if (s.auth === 'oauth') return <Chip tone="warn">Needs sign-in</Chip>;
    return <Chip tone="warn">Needs a token</Chip>;
  };

  const signInLabel = (s: PersonalMcpServer): string => {
    if (s.connected) return 'Sign in again';
    return 'Sign in';
  };

  const count = data.mine.length;
  const countLabel = count === 1 ? '1 server' : `${count} servers`;

  return (
    <div className="mx-auto max-w-[1000px]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-[13px] text-ink-muted">{countLabel} · Each runs on your own sign-in, in your own sessions</span>
        {data.can_add && (
          <button type="button" onClick={() => setAdding(true)} className="inline-flex h-10 items-center gap-1.5 rounded-act bg-ink px-4 text-[13px] font-semibold text-white hover:bg-black whitespace-nowrap">
            <Plus size={15} /> Add your own
          </button>
        )}
      </div>
      <div className="mt-4">
        {count === 0 && (
          <EmptyState title="No MCP servers yet" reason="Add your own. It runs on your own sign-in, in your own sessions." />
        )}
        {count > 0 && (
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-line">
                <th className={HEAD}>Server</th>
                <th className={`${HEAD} hidden md:table-cell`}>Sign-in</th>
                <th className={HEAD}>Status</th>
                <th className={HEAD}><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {data.mine.map((s) => (
                <tr key={s.key} className="border-b border-line-soft">
                  <td className={CELL}>
                    <span className="flex items-center gap-3">
                      <RouteMark glyph={s.label.charAt(0).toUpperCase()} />
                      <span className="min-w-0">
                        <span className="block font-medium">{s.label}</span>
                        <span className="block truncate text-[12.5px] text-ink-dim">{s.description || s.host}</span>
                      </span>
                    </span>
                  </td>
                  <td className={`${CELL} hidden text-ink-muted md:table-cell`}>{AUTH_LABEL[s.auth]}</td>
                  <td className={`${CELL} w-[1%] whitespace-nowrap`}>{statusOf(s)}</td>
                  <td className={`${CELL} w-[1%] whitespace-nowrap`}>
                    <span className="flex items-center justify-end gap-2">
                      <Action size="sm" variant="quiet" disabled={!s.connected} reason="Sign in first" onClick={() => setPermsFor({ key: s.key, label: s.label })}>Permissions</Action>
                      {s.auth === 'oauth' && (
                        <Action size="sm" variant="quiet" disabled={signingIn === s.key} reason="Waiting for the provider" onClick={() => signIn(s)}>{signInLabel(s)}</Action>
                      )}
                      <Action size="sm" variant="quiet" disabled={busy === s.key} reason="Removing" onClick={() => remove(s)}>Remove</Action>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {data.company.length > 0 && (
        <div className="mt-8">
          <div className="text-[13px] text-ink-muted">Company servers you use · Set what the AI may do in each, for your own chats</div>
          <table className="mt-3 w-full border-collapse">
            <tbody>
              {data.company.map((s) => (
                <tr key={s.key} className="border-b border-line-soft">
                  <td className={CELL}>
                    <span className="flex items-center gap-3">
                      <RouteMark glyph={s.label.charAt(0).toUpperCase()} />
                      <span className="min-w-0">
                        <span className="block font-medium">{s.label}</span>
                        <span className="block truncate text-[12.5px] text-ink-dim">{s.host}</span>
                      </span>
                    </span>
                  </td>
                  <td className={`${CELL} w-[1%] whitespace-nowrap`}><Chip>Company</Chip></td>
                  <td className={`${CELL} w-[1%] whitespace-nowrap`}>
                    <span className="flex justify-end">
                      <Action size="sm" variant="quiet" onClick={() => setPermsFor({ key: s.key, label: s.label })}>Permissions</Action>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {permsFor && <McpPermissions server={permsFor} onClose={() => setPermsFor(null)} />}
      {removing && (
        <RemoveMcpConfirm
          scope="mine"
          label={removing.label}
          auth={removing.auth}
          connected={removing.connected}
          busy={busy === removing.key}
          onCancel={() => setRemoving(null)}
          onConfirm={() => confirmRemove(removing)}
        />
      )}
      {adding && <ServerDialog onClose={() => setAdding(false)} onDone={() => { setAdding(false); reload(); }} />}
    </div>
  );
};

export default EmployeeMcpPanel;
